import {ReactElement, useContext, useEffect, useMemo, useState} from 'react';
import {useParams} from 'react-router-dom';
import {BudgetingType} from '../../../../BudgetingType';
import {getBudgetYearForProperty, useProperties} from '../../../../contexts/properties/PropertiesContext';
import {useSettings} from '../../../../contexts/settings/SettingsContext';
import {OriginalRevenueOperationalMetricValidKey, useGetForecastLocksLazyQuery, useGetOperationalMetricForPropertyYearLazyQuery, useUpsertOpMetricValuesMutation, VersionType} from '../../../../__generated__/generated_types';
import "../styles/operational.scss";

import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../styles/zendesk-garden/ViziblyZDGTheme";

import {Tabs} from "@zendeskgarden/react-tabs";

import * as workflowCSS from "../../../../styles/workflows/workflowCSS.module.scss";
import * as workflowHeaderCSS from "../../../../components/workflows/workflow-header/styles/css.module.scss";

import WorkflowHeader from "../../../../components/workflows/workflow-header/WorkflowHeader";
import WorkflowNavToggle from "../../../../components/workflows/workflow-nav-toggle/WorkflowNavToggle";
import WorkflowPageLabel from "../../../../components/workflows/workflow-page-label/WorkflowPageLabel";
import WorkflowStepNav from "../../../../components/workflows/workflow-step-nav/WorkflowStepNav";

import WorkflowYearTabs, {
    WORKFLOW_YEAR_TAB_BUDGET
} from "../../../../components/workflows/workflow-year-tabs/WorkflowYearTabs";
import {IWorkflowPageProps, shouldUpdateWorkflowTab} from "../../logic/workflows";
import { IVZHotTableRow } from '../../../../components/vz-ui/vz-hot-table/VZHotTable';
import { VZHotTableConfig } from '../../../../components/vz-ui/vz-hot-table/vz-hot-table-config';
import VZSaveStatus from '../../../../components/vz-ui/vz-save-status/VZSaveStatus';
import { toast } from 'react-toastify';
import { useVersions } from '../../../../contexts/versions/VersionsContext';
import { OperationalFinancialMetrics } from '../operational-metrics';
import { getVersionLabel } from '../utils/operational-metric-helpers';
import OperationalVZHotTable from '../OperationalVZHotTable';
import {getMonths} from '../../../../utils/months-helper';
import {AuthContext} from "../../../../contexts/AuthContext";

export default function EarlyTerminations(props:IWorkflowPageProps): ReactElement {
	const { type } = useParams() as { type: BudgetingType; propertyId: string };
    const {versions} = useVersions();
	const { propertyId, currentProperty } = useProperties();
    const auth = useContext(AuthContext);
    const budgetYear = getBudgetYearForProperty(currentProperty, auth.budgetYear);

	const { year } = useSettings();

	const currentVersionYear = type == BudgetingType.BUDGET ? year + 1 : year;
    const versionType = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;

    const [lastSaved, setLastSaved] = useState<Date>(new Date());

    const [getForecastLocks, {data: dataForecastLocks}] = useGetForecastLocksLazyQuery({
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        getForecastLocks({
            variables: {
                propertyId,
                budgetYear: budgetYear,
            }
        });
    }, [budgetYear]);

    const [getMetricData, {data: metricData, loading: metricDataLoading}] = useGetOperationalMetricForPropertyYearLazyQuery({fetchPolicy: "network-only"});
    const [upsertOpMetricValues, {loading: savingValues}] = useUpsertOpMetricValuesMutation({ignoreResults: true});

    /**
     * Update selected tab, which updates display, on type change
     */
    useEffect(
            () => {
                const updateWorkflowTab = shouldUpdateWorkflowTab(type, selectedTab);
                if(updateWorkflowTab != false){
                    setSelectedTab(updateWorkflowTab as string);
                }
            },
            [type]
    );

    useEffect(() => {
        if (currentProperty) {
            getMetricData({
                variables: {
                    propertyId: currentProperty.id,
                    budgetYear: currentProperty.budgetYear,
                    metric: OriginalRevenueOperationalMetricValidKey.EarlyTerminationCount,
                }
            });
        }
    }, [currentProperty, type]);

    const [selectedTab, setSelectedTab] = useState(WORKFLOW_YEAR_TAB_BUDGET);

    const {tableData, tableConfig} = useMemo<{tableData: IVZHotTableRow[], tableConfig: VZHotTableConfig}>(() => {
        const tableData: IVZHotTableRow[] = [];
        const tableConfig = new VZHotTableConfig();

        if (!metricData?.originalRevenueOperationalMetricValuesForPropertyYear || !dataForecastLocks?.forecastLocks || !currentProperty) {
            return {tableData, tableConfig};
        }

        let tableLocked = true;

        if (type == BudgetingType.BUDGET) {
            tableLocked = dataForecastLocks?.forecastLocks?.property.budgetLocked;
        } else {
            tableLocked = dataForecastLocks?.forecastLocks?.property.reforecastLocked;
        }

        const sortedData = metricData.originalRevenueOperationalMetricValuesForPropertyYear.sortByDescending("year");

        for (const versionData of sortedData) {
            if (versionData.version == VersionType.Budget && versionType == VersionType.Reforecast) {
                continue;
            }

            const rowType = versionData.version == versionType ? "generic" : "historical";

            tableData.push({
                label: getVersionLabel(versionData.version, versionData.year, currentProperty),
                rowType,
                values: versionData.values.map(x => x == null ? 0 : +x),
            });
        }

        let writeableColumnsStart = 1;

        if (type == BudgetingType.REFORECAST) {
            writeableColumnsStart = currentProperty.reforecastStartMonthIndex + 1;
        }

        tableConfig.tableSummaryFunction = "sum";
        tableConfig.applyToColumn(0, {isBoldText: true});
        tableConfig.applyToColumnAndFollowing(writeableColumnsStart, {isReadOnly: tableLocked, format: "count"});
        tableConfig.applyToColumn(13, {isReadOnly: true, isGreyBackground: true, isBoldText: true});
        tableConfig.applyToRowAndFollowing(1, {isReadOnly: true, isGreyBackground: true, isBoldText: true});
        tableConfig.applyToRow(1, {isThickBorderTop: true});

        return {tableData, tableConfig};
    }, [metricData, dataForecastLocks, currentProperty]);

	return (
        <ThemeProvider theme={ViziblyTheme}>
            <div className={`${workflowCSS.workflowContainer} ${workflowCSS.revenueScreen}`}>
                <WorkflowHeader>
                    <WorkflowHeader.LeftCol>
                        <div className={workflowHeaderCSS.rowItemsContainer}>
                            <WorkflowNavToggle inUserWorkflow={props.inUserWorkflow}/>
                            <WorkflowPageLabel label="Early Terminations"/>
                        </div>
                    </WorkflowHeader.LeftCol>
                    <WorkflowHeader.RightCol>
                        <VZSaveStatus isSaving={savingValues} lastSaved={lastSaved} />
                        <WorkflowStepNav onPreviousClick={props.onPreviousClick} onNextClick={props.onNextClick} inUserWorkflow={props.inUserWorkflow}/>
                    </WorkflowHeader.RightCol>
                </WorkflowHeader>

                <div className={workflowCSS.bodyContainer}>
                    { currentProperty &&
                        <>
                            <Tabs selectedItem={selectedTab} onChange={setSelectedTab} className={workflowCSS.tabsContainer}>
                                <WorkflowYearTabs
                                    property={currentProperty}
                                    selectedTab={selectedTab}
                                    rawForecastLocks={dataForecastLocks}
                                />
                            </Tabs>

                            <div className={workflowCSS.tableWrapper}>
                                <OperationalVZHotTable
                                    data={tableData}
                                    colWidths={[80]}
                                    colHeaders={["Year", ...getMonths(currentProperty), "Total"]}
                                    tableConfig={tableConfig}
                                    isSearchable={false}
                                    isDataLoading={metricDataLoading}
                                    summaryColumns={[
                                        {
                                            col: 13,
                                            summaryFunction: "sum",
                                        },
                                    ]}
                                    onChange={(changes) => {
                                        if (!currentProperty) {
                                            return;
                                        }

                                        const monthIndexPayload = [];
                                        const valuesPayload = [];

                                        for (const [rowIndex, monthIndex, value] of changes) {
                                            const row = tableData[rowIndex];

                                            if (!row) {
                                                continue;
                                            }

                                            monthIndexPayload.push(monthIndex);
                                            valuesPayload.push(String(value));
                                        }

                                        const typeToVersion = type == BudgetingType.BUDGET ? VersionType.Budget : VersionType.Reforecast;
                                        const versionId = versions?.valueVersions.find(x => x.year == currentVersionYear && x.type == typeToVersion)

                                        if (versionId) {
                                            upsertOpMetricValues({
                                                variables: {
                                                    propertyId: currentProperty.id,
                                                    versionId: versionId.id,
                                                    fieldName: "earlyTerminationCount",
                                                    monthIndexes: monthIndexPayload,
                                                    values: valuesPayload,
                                                    budgetYear: budgetYear
                                                },
                                            }).then((data) => {
                                                if (data.data?.upsertOpMetricValues) {
                                                    setLastSaved(new Date());
                                                } else {
                                                    toast.error("There was a problem saving.");
                                                }
                                            });
                                        }

                                    }}
                                />
                                {!currentProperty?.isAcquisitionProperty &&
                                    <OperationalFinancialMetrics type={type} budgetYear={budgetYear}/>
                                }
                            </div>
                        </>
                    }
                </div>
            </div>
        </ThemeProvider>
	);
}
